import { classes } from '@/uiPrimitives/base/classes.helpers';
import {
	cellWithBlockEndBorderCSS,
	cellWithInlinePaddingCSS,
	tableCSS,
	tableFontStyleCSS,
	tableHeaderCSS,
} from '../cbsGraph.css';
import { getLineGraphProps } from './helpers/getLineGraphProps';
import {
	WebAPICBSPeriodType,
	WebAPILineGraphChartSlicerData,
} from '../../../globals/webApiTypes/elements/WebAPICBS';
import { translateInline } from '@/globals/translate/translateInline';
import {
	textBlock,
	textInline,
	textStyle,
} from '@/uiPrimitives/typography/text';
import { box } from '@/uiPrimitives/layout/box';
import { position } from '@/uiPrimitives/layout/position';
import { periodTickFormatter } from '../helpers/periodTickFormatter';
import { formatNumber } from '@dop/shared/helpers/string';
import { inlineBlock } from '@/uiPrimitives/utility/display.css';

const periodMapping = {
	Maanden: translateInline({ en: 'Months', nl: 'Maanden' }),
	Kwartalen: translateInline({ en: 'Quarters', nl: 'Kwartalen' }),
	Jaar: translateInline({ en: 'Year', nl: 'Jaar' }),
} as const satisfies Record<WebAPICBSPeriodType, string>;

export const LineGraphDataTable = ({
	dimensionInfo,
	periodType,
	unit,
	decimals,
	periodDatesInRange,
	valuesByDimension,
}: WebAPILineGraphChartSlicerData & ReturnType<typeof getLineGraphProps>) => {
	const columnCount = dimensionInfo.length + 1;

	const columnInlineSize = `${Math.floor(100 / columnCount)}%`;

	return (
		<table className={classes(tableCSS)}>
			<thead
				className={classes(
					position({ position: 'sticky', insetBlockStart: '0' }),
				)}
			>
				<tr>
					<th
						scope="column"
						className={classes(tableHeaderCSS)}
						style={{ inlineSize: columnInlineSize }}
					>
						<span
							className={classes(
								textStyle({ textAlign: 'center', fontWeight: 'bold' }),
								tableFontStyleCSS,
							)}
						>
							{periodMapping[periodType]}
						</span>
					</th>
					{dimensionInfo.map(
						({ dimensionLabel, dimensionKey, chartColor }, index) => (
							<th
								className={classes(tableHeaderCSS, cellWithInlinePaddingCSS)}
								key={`${dimensionKey}_${index}`}
								style={{ inlineSize: columnInlineSize }}
							>
								<span
									className={classes(
										textStyle({
											textAlign: 'center',
											fontWeight: 'bold',
										}),
										tableFontStyleCSS,
									)}
								>
									<span
										className={classes(
											box({ paddingInlineEnd: '-2 | smaller' }),
											inlineBlock,
										)}
									>
										{dimensionLabel}
									</span>
									<span
										className={classes(
											inlineBlock,
											textInline({
												color: 'positiveMuted',
												fontWeight: 'normal',
											}),
										)}
									>
										{`(${unit})`}
									</span>
								</span>
								<i
									className={classes(
										box({
											borderBlockEndStyle: 'solid',
											borderWidth: '3px',
										}),
										position({ position: 'relative' }),
									)}
									style={{
										insetBlockEnd: '-9px',
										borderColor: chartColor.color,
									}}
								/>
							</th>
						),
					)}
				</tr>
			</thead>
			<tbody>
				{periodDatesInRange.map((periodDate, i) => (
					<tr key={`${periodDate.toString()}_${i}`}>
						<td
							className={classes(
								cellWithBlockEndBorderCSS,
								cellWithInlinePaddingCSS,
							)}
						>
							<span
								className={classes(
									textBlock({
										verticalAlign: 'top',
										textAlign: 'end',
									}),
									tableFontStyleCSS,
								)}
							>
								{periodTickFormatter({ periodType, singleLineWithYear: true })(
									periodDate,
									i,
									periodDatesInRange,
								)}
							</span>
						</td>
						{dimensionInfo.map(({ dimensionKey }, i) => {
							const value =
								valuesByDimension[dimensionKey][periodDate.toString()];

							return (
								<td
									className={classes(
										cellWithBlockEndBorderCSS,
										cellWithInlinePaddingCSS,
									)}
									key={`${dimensionKey}_${i}`}
								>
									<span
										className={classes(
											textBlock({ textAlign: 'end' }),
											tableFontStyleCSS,
										)}
									>
										{formatNumber({
											decimals,
											number: value,
										})}
									</span>
								</td>
							);
						})}
					</tr>
				))}
			</tbody>
		</table>
	);
};
